import { useCallback, useContext, useEffect, useState } from "react";
import moment from "moment";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Pagination,
  Chip,
  Tooltip,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import SearchIcon from "@mui/icons-material/Search";

import { AuthContext } from "../../shared/context/auth-context";
import { useHttpClient } from "../../shared/hooks/http-hook";
import { useLoading } from "../../shared/context/LoadingContext";

const EVENTOS = {
  LOGIN: "Entrada",
  LOGOUT: "Saída",
  LOGIN_FALHADO: "Tentativa falhada",
};

/* Verde para a entrada, cinzento para a saída, vermelho para a falha. A falha é
   a única que precisa de saltar à vista: é a que pode ser alguém a tentar
   entrar numa conta que não é sua. */
const CORES = {
  LOGIN: { color: "#0f7b3f", backgroundColor: "#e6f6ec" },
  LOGOUT: { color: "#4a5568", backgroundColor: "#edf2f7" },
  LOGIN_FALHADO: { color: "#b42318", backgroundColor: "#fee4e2" },
};

const LogsAutenticacao = () => {
  const auth = useContext(AuthContext);
  const { sendRequest } = useHttpClient();
  const { startLoading, stopLoading } = useLoading();

  const [acessos, setAcessos] = useState([]);
  const [startDate, setStartDate] = useState(moment().subtract(7, "days"));
  const [endDate, setEndDate] = useState(moment());
  const [evento, setEvento] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchAcessos = useCallback(
    async (inicio, fim, tipoEvento, pagina) => {
      startLoading();
      try {
        const formData = new FormData();
        formData.append("startDate", inicio.format("YYYY-MM-DD"));
        formData.append("endDate", fim.format("YYYY-MM-DD"));
        formData.append("evento", tipoEvento || "");
        formData.append("page", pagina);

        const resposta = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/clinicalogs/acessos`,
          "PATCH",
          formData,
          { Authorization: "Bearer " + auth.token },
        );

        setAcessos(resposta?.acessos || []);
        setTotalPages(resposta?.pagination?.pages || 1);
      } catch (err) {
        console.error("Erro ao carregar registos de autenticação:", err);
      } finally {
        stopLoading();
      }
    },
    [auth.token, sendRequest, startLoading, stopLoading],
  );

  useEffect(() => {
    fetchAcessos(startDate, endDate, evento, page);
    // Só à entrada e quando a página muda; os filtros têm o seu próprio botão.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const procurar = () => {
    setPage(1);
    fetchAcessos(startDate, endDate, evento, 1);
  };

  return (
    <>
      <div className="logsHeader">
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DesktopDatePicker
            label="Data Inicial*"
            inputFormat="DD-MM-YYYY"
            renderInput={(params) => <TextField {...params} />}
            onChange={(valor) => setStartDate(valor)}
            value={startDate}
          />
        </LocalizationProvider>

        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DesktopDatePicker
            label="Data Final*"
            inputFormat="DD-MM-YYYY"
            renderInput={(params) => <TextField {...params} />}
            onChange={(valor) => setEndDate(valor)}
            value={endDate}
          />
        </LocalizationProvider>

        <div></div>

        <FormControl>
          <InputLabel>Tipo</InputLabel>
          <Select
            value={evento}
            label="Tipo"
            onChange={(e) => setEvento(e.target.value)}
          >
            <MenuItem value="">
              <em>Todos</em>
            </MenuItem>
            {Object.entries(EVENTOS).map(([valor, etiqueta]) => (
              <MenuItem key={valor} value={valor}>
                {etiqueta}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* As mesmas classes do separador ao lado. A que estava aqui
            (`tabs-container-top-button`) não existia em folha de estilo
            nenhuma, e o botão saía como texto solto. */}
        <div className="logs__searchButtons__container">
          <div
            className="logs__searchButtons__btn logs__searchButtons__searchBtn"
            onClick={procurar}
          >
            <SearchIcon className="logs__searchButtons__searchBtn__icon" />
            <span className="logs__searchButtons__searchBtn__text">
              Pesquisar
            </span>
          </div>
        </div>
      </div>

      {/* O aviso não é decoração. Sem ele, quem lê esta lista conclui que quem
          não tem saída registada continua com sessão aberta — e isso é falso na
          maioria dos casos. */}
      <p className="logsAutenticacao__aviso">
        A saída só fica registada quando a pessoa carrega em «Sair». Fechar o
        separador, o computador desligar-se ou a sessão expirar não chegam ao
        servidor — por isso muitas entradas não terão saída correspondente.
      </p>

      <table className="logsAutenticacao__tabela">
        <thead>
          <tr>
            <th>Data e hora</th>
            <th>Utilizador</th>
            <th>Tipo</th>
            <th>Unidade</th>
            <th>IP</th>
            <th>Observação</th>
          </tr>
        </thead>
        <tbody>
          {acessos.length === 0 && (
            <tr>
              <td colSpan={6} className="logsAutenticacao__vazio">
                Sem registos no período escolhido.
              </td>
            </tr>
          )}
          {acessos.map((a) => (
            <tr key={a._id}>
              <td>{moment(a.createdAt).format("DD-MM-YYYY HH:mm:ss")}</td>
              <td>
                <Tooltip title={a.email || ""}>
                  <span>{a.utilizador?.name || a.email}</span>
                </Tooltip>
              </td>
              <td>
                <Chip
                  size="small"
                  label={EVENTOS[a.evento] || a.evento}
                  sx={CORES[a.evento]}
                />
              </td>
              <td>
                {a.estabelecimento
                  ? `${a.estabelecimento.nome} (${a.estabelecimento.codigo})`
                  : "—"}
              </td>
              <td className="logsAutenticacao__ip">{a.ip || "—"}</td>
              <td>{a.motivo || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {totalPages > 1 && (
        <Pagination
          count={totalPages}
          page={page}
          onChange={(e, valor) => setPage(valor)}
          sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}
        />
      )}
    </>
  );
};

export default LogsAutenticacao;
